import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusCode } from '../../components/Alert.tsx';
import MediaController from '../../controllers/MediaController.ts';
import RecipesController from '../../controllers/RecipesController.ts';
import { Recipe } from '../../models/recipes/recipe.ts';
import { RecipeAliment } from '../../models/recipes/recipeAliment.ts';
import { useAlert } from '../alertProvider.tsx';

type RecipeContextType = {
    isLoaded?: boolean;
    recipe: Recipe;
    insertRecipe: (entity: Recipe, file: any) => void;
    updateRecipe: (entity: Recipe, file: any) => void;
};

const RecipeContext = createContext<RecipeContextType>({} as RecipeContextType);

export const RecipeContextProvider = ({ children, id }: { children: any, id?: any }) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [recipe, setRecipe] = useState<Recipe>({ recipeID: 0, name: '', userID: 0, recipeAliments: [] as RecipeAliment[] } as Recipe);
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const getRecipeByID = async () => {
        if (id == "create") { setIsLoaded(true); return; }
        try {
            const result = await RecipesController.getByID(id);
            if (result.notificationStatus == StatusCode.success)
                setRecipe(result);
            else showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error fetching recipe:', error);
        } finally {
            setIsLoaded(true);
        }
    }

    const insertRecipe = async (entity: Recipe, file: any) => {
        try {
            const result = await RecipesController.post(entity);
            if (result.notificationStatus == StatusCode.success) {
                if (file == null) {
                    navigate(-1);
                    return;
                }
                const fileResult = await MediaController.post('Recipe', result.recipeID, file);
                if (fileResult.notificationStatus == StatusCode.success)
                    navigate(-1);
                else
                    showAlert(fileResult.notificationStatus, fileResult.notificationMessage);
            }
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error inserting recipe:', error);
        }
    }

    const updateRecipe = async (entity: Recipe, file: any) => {
        try {
            const result = await RecipesController.put(entity);
            if (result.notificationStatus == StatusCode.success) {
                if (file == null) {
                    navigate(-1);
                    return;
                }
                const fileResult = await MediaController.post('Recipe', entity.recipeID, file);
                if (fileResult.notificationStatus == StatusCode.success)
                    navigate(-1);
                else
                    showAlert(fileResult.notificationStatus, fileResult.notificationMessage);
            }
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error updating recipe:', error);
        }
    }

    useEffect(() => {
        getRecipeByID();
    }, [id]);

    const context: RecipeContextType = {
        isLoaded,
        recipe,
        insertRecipe,
        updateRecipe
    }

    return (
        <RecipeContext.Provider value={context}>
            {children}
        </RecipeContext.Provider>
    )
}

export const useRecipeContext = () => useContext(RecipeContext)
